import { AppData, Metadata } from '@lightningjs/sdk';
import {
  convivaAppTracker,
  ConvivaDeviceMetadata,
  setUserId,
  trackButtonClick,
  trackCustomEvent,
  trackPageView,
} from '@convivainc/conviva-js-appanalytics';
import { DeviceInfo } from 'models/platforms/deviceInfo';
import { AbstractDeviceIntegration } from 'config/platforms/AbstractDeviceIntegration';
import StorageService from 'services/StorageService';
import ConvivaVideo from 'services/analytics/ConvivaVideo';
import { Manufacturer } from 'models/platforms/manufacturer';
import MParticleAnalytics from 'services/analytics/MParticle';
import { ReportableError } from 'services/analytics/reportingServicePage';
import LoginRadiusService from 'services/LoginRadiusService';

export enum ButtonType {
  BUTTON = 'button',
  RADIO = 'radio',
  RESET = 'reset',
  SUBMIT = 'submit',
}

export type ButtonClickProps = Parameters<typeof trackButtonClick>[0];

type ConstructorArgs = {
  storageService: StorageService;
  deviceIntegration: AbstractDeviceIntegration;
  mParticle: MParticleAnalytics;
  loginRadiusService: LoginRadiusService | null;
};

export default class ConvivaAnalytics {
  static readonly serviceName = 'ConvivaAnalytics';

  video: ConvivaVideo;

  private mParticle: MParticleAnalytics;

  static async AsyncConstruct({
    deviceIntegration,
    storageService,
    mParticle,
    loginRadiusService,
  }: ConstructorArgs) {
    if (AppData === undefined) {
      throw new Error('AppData not set');
    }

    const deviceInfo = await deviceIntegration.getDeviceInfo();
    return new ConvivaAnalytics(
      deviceInfo,
      storageService,
      mParticle,
      loginRadiusService,
    );
  }

  private constructor(
    deviceInfo: DeviceInfo,
    storageService: StorageService,
    mParticle: MParticleAnalytics,
    loginRadiusService: LoginRadiusService | null,
  ) {
    if (AppData === undefined) {
      throw new Error('AppData not set');
    }

    this.video = new ConvivaVideo(
      deviceInfo,
      storageService,
      loginRadiusService,
    );

    let deviceCategory: ConvivaDeviceMetadata['DeviceCategory'];
    switch (deviceInfo.manufacturer) {
      case Manufacturer.SAMSUNG:
        deviceCategory = 'SAMSUNGTV';
        break;
      case Manufacturer.LG:
        deviceCategory = 'LGTV';
        break;
      case Manufacturer.VIZIO:
        deviceCategory = 'VIZIOTV';
        break;
      case Manufacturer.COMCAST:
        deviceCategory = 'LNX';
        break;
      default:
        deviceCategory = 'WEB';
    }

    const deviceMetadata: ConvivaDeviceMetadata = {
      DeviceBrand: deviceInfo.manufacturer,
      DeviceCategory: deviceCategory,
      DeviceModel: deviceInfo.model,
      DeviceType: 'SmartTV',
      OperatingSystemVersion: deviceInfo.osVersion,
    };

    convivaAppTracker({
      appId: AppData.conviva.playerName,
      convivaCustomerKey: AppData.conviva.customerKey,
      appVersion: Metadata.appVersion(),
      deviceMetadata,
    });

    this.mParticle = mParticle;
    this.updateUser();
  }

  updateUser() {
    setUserId(this.mParticle.mpid);
  }

  reportPageView(title: string) {
    trackPageView({ title });
  }

  reportButtonClick(props: ButtonClickProps) {
    trackButtonClick({
      ...(props as any),
      timestamp: new Date().getTime(),
    });
  }

  reportError(error: ReportableError) {
    const { name, message, stack } = error.error as Error;
    const code = error.code;

    trackCustomEvent({
      name,
      timestamp: new Date().getTime(),
      data: {
        message,
        stack,
        code,
      },
    });
  }
}
